// ============================================================
// FILE: src/app/shared/live-session-schema.builder.ts
// PURPOSE: Builds all JSON-LD schema objects for the live
//          sessions page from the live MongoDB event data.
// ============================================================

export interface SessionSchemaInput {
  _id: string;
  title: string;
  description: string;
  sessions: Array<{
    id?: string;
    date?: string | Date; 
    startTime?: string;
    endTime?: string;
    timezone?: string;
  }>;
}

export class LiveSessionSchemaBuilder {

  private static readonly PAGE_URL  = 'https://academy.nexgencoaches.com/live-session';
  private static readonly ORG_URL   = 'https://nexgencoaches.com';
  private static readonly ORG_NAME  = 'NEXGEN Executive Coaching Academy';
  private static readonly ORG_LOGO  = 'https://nexgencoaches.com/assets/nexgen-logo.png';
  private static readonly OG_IMAGE  = 'https://nexgencoaches.com/assets/og-live-sessions.jpg';

  /**
   * Build the complete @graph array for this page.
   * Pass in only UPCOMING events (already filtered).
   */
  static buildGraph(upcomingEvents: SessionSchemaInput[]): object[] {
    const graph: object[] = [];

    // 1. Organization (site-wide anchor — always include)
    graph.push(this.buildOrganization());

    // 2. WebPage
    graph.push(this.buildWebPage());

    // 3. BreadcrumbList
    graph.push(this.buildBreadcrumb());

    // 4. One Event schema per upcoming session
    upcomingEvents.forEach(ev => {
      const eventSchema = this.buildEvent(ev);
      if (eventSchema) graph.push(eventSchema);
    });

    // 5. FAQPage (static — based on the site's actual FAQ content)
    graph.push(this.buildFaq());

    return graph;
  }

  // ── 1. Organization ───────────────────────────────────────
  static buildOrganization(): object {
    return {
      '@type': 'EducationalOrganization',
      '@id': `${this.ORG_URL}/#organization`,
      'name': this.ORG_NAME,
      'url': this.ORG_URL,
      'logo': {
        '@type': 'ImageObject',
        'url': this.ORG_LOGO
      },
      'sameAs': [
        'https://www.linkedin.com/company/nexgen-executive-coaching-academy',
        'https://www.facebook.com/nexgencoaches',
        'https://twitter.com/nexgencoaches'
      ],
      'contactPoint': {
        '@type': 'ContactPoint',
        'telephone': '+1-248-270-8323',
        'contactType': 'customer service',
        'areaServed': 'US',
        'availableLanguage': 'English'
      }
    };
  }

  // ── 2. WebPage ────────────────────────────────────────────
  static buildWebPage(): object {
    return {
      '@type': 'WebPage',
      '@id': `${this.PAGE_URL}#webpage`,
      'url': this.PAGE_URL,
      'name': 'Live Sessions & Webinars | NEXGEN Executive Coaching',
      'description': 'Attend free live info sessions and webinars at NEXGEN Executive Coaching Academy. Learn about ICF certification programs. Reserve your spot.',
      'inLanguage': 'en-US',
      'isPartOf': {
        '@type': 'WebSite',
        '@id': `${this.ORG_URL}/#website`,
        'url': this.ORG_URL,
        'name': this.ORG_NAME,
        'publisher': { '@id': `${this.ORG_URL}/#organization` }
      },
      'about': {
        '@id': `${this.ORG_URL}/#organization`
      },
      'primaryImageOfPage': {
        '@type': 'ImageObject',
        'url': this.OG_IMAGE,
        'width': 1200,
        'height': 630
      }
    };
  }

  // ── 3. BreadcrumbList ─────────────────────────────────────
  static buildBreadcrumb(): object {
    return {
      '@type': 'BreadcrumbList',
      '@id': `${this.PAGE_URL}#breadcrumb`,
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Home',
          'item': this.ORG_URL + '/'
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': 'Academy',
          'item': 'https://academy.nexgencoaches.com/'
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'name': 'Live Sessions',
          'item': this.PAGE_URL
        }
      ]
    };
  }

  // ── 4. Event (one per upcoming session) ──────────────────
  static buildEvent(ev: SessionSchemaInput): object | null {
    const session = ev.sessions?.[0];
    if (!session?.date || !session?.startTime || !session?.endTime) {
      return null; // Can't build event without date/time
    }

    // Build ISO 8601 datetime strings with EST offset (-05:00 / -04:00)
    // Using -05:00 for EST. Adjust to -04:00 during EDT if needed.
    const startISO = `${session.date}T${this.padTime(session.startTime)}:00-05:00`;
    const endISO   = `${session.date}T${this.padTime(session.endTime)}:00-05:00`;

    return {
      '@type': 'Event',
      '@id': `${this.PAGE_URL}#event-${ev._id}`,
      'name': ev.title,
      'description': this.stripHtml(ev.description),
      'startDate': startISO,
      'endDate': endISO,
      'duration': 'PT60M',
      'eventStatus': 'https://schema.org/EventScheduled',
      'eventAttendanceMode': 'https://schema.org/OnlineEventAttendanceMode',
      'isAccessibleForFree': true,
      'inLanguage': 'en-US',
      'image': this.OG_IMAGE,
      'url': this.PAGE_URL,
      'offers': {
        '@type': 'Offer',
        'name': 'Free Registration',
        'price': '0',
        'priceCurrency': 'USD',
        'availability': 'https://schema.org/InStock',
        'validFrom': new Date().toISOString().split('T')[0],
        'url': this.PAGE_URL
      },
      'organizer': {
        '@type': 'EducationalOrganization',
        '@id': `${this.ORG_URL}/#organization`,
        'name': this.ORG_NAME,
        'url': this.ORG_URL
      },
      'performer': [
        {
          '@type': 'Person',
          'name': 'Lyssa deHart',
          'jobTitle': 'Master Certified Coach (MCC), LICSW',
          'url': `${this.ORG_URL}/about-us/`,
          'sameAs': 'https://www.linkedin.com/in/lyssadehart'
        },
        {
          '@type': 'Person',
          'name': 'Jake Mannino',
          'jobTitle': 'Professional Certified Coach (PCC), MBA',
          'url': `${this.ORG_URL}/about-us/`,
          'sameAs': 'https://www.linkedin.com/in/jakemannino'
        }
      ],
      'location': {
        '@type': 'VirtualLocation',
        'url': 'https://zoom.us',
        'name': 'Zoom Webinar'
      },
      'about': [
        {
          '@type': 'Thing',
          'name': 'ICF Coaching Certification'
        },
        {
          '@type': 'Thing',
          'name': 'Executive Coaching Programs'
        },
        {
          '@type': 'Thing',
          'name': 'ACC PCC Credentials'
        }
      ]
    };
  }

  // ── 5. FAQPage ────────────────────────────────────────────
  static buildFaq(): object {
    return {
      '@type': 'FAQPage',
      '@id': `${this.PAGE_URL}#faq`,
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Are the NEXGEN live coaching sessions really free?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Yes. All NEXGEN live info sessions are completely free to attend. No credit card is required and there is no obligation to enroll in any program.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What is covered in a NEXGEN live session?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Each 60-minute session covers: a full overview of the NEXGEN executive coaching program model, the ICF credential pathway (ACC and PCC requirements explained in plain language), and an open live Q&A with ICF-certified faculty including Lyssa deHart (MCC) and Jake Mannino (PCC).'
          }
        },
        {
          '@type': 'Question',
          'name': 'How do I join a NEXGEN live session?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Reserve your spot by clicking "Reserve My Spot" on this page, entering your name and email address, and submitting the form. You will receive a Zoom link and confirmation by email immediately.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How long is each live session?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Each session is 60 minutes long and is conducted live via Zoom.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Who hosts the NEXGEN live sessions?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Sessions are hosted by Lyssa deHart (LICSW, MCC) and Jake Mannino (MBA, PCC). Lyssa is a Master Certified Coach who has trained over 10,000 coaches and is an author and podcast host. Jake is an executive leader at Microsoft overseeing a $1B+ organization.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Will there be a sales pitch during the live session?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'No. NEXGEN live sessions are designed as honest informational conversations — not sales calls. You can attend, ask questions, and decide for yourself with zero pressure.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What ICF credentials are covered in the live session?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The session covers the ICF ACC (Associate Certified Coach) and PCC (Professional Certified Coach) credential pathways, including training hour requirements, mentor coaching, and how the NEXGEN Level 1 and Level 2 programs align with each credential.'
          }
        }
      ]
    };
  }

  // ── Utilities ─────────────────────────────────────────────

  /** Ensure time is in HH:MM format */
  private static padTime(time: string): string {
    const parts = String(time).split(':');
    const h = String(parts[0] || '00').padStart(2, '0');
    const m = String(parts[1] || '00').padStart(2, '0');
    return `${h}:${m}`;
  }

  /** Strip HTML tags from description strings */
  private static stripHtml(html: string): string {
    return String(html || '').replace(/<[^>]*>/g, '').trim();
  }
}